import {
  Checkbox as _Checkbox,
  DialogContent,
  DialogTitle as _DialogTitle,
  FormControlLabel,
  styled,
  RadioGroup,
  Radio,
  FormControl,
  FormLabel,
  formLabelClasses,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { ChevronIcon } from '../Icons'
import { HBButton as _HBButton } from '../Material/Card/HBButton'
import { LinkChip as _LinkChip } from '../Material/Chips'
import { StyledDialog } from '../Material/Modal/Modal'
import { useDeclineModalDeclineReasonsQuery } from './DeclineModal.generated'
import * as Sentry from '@sentry/browser'
import {
  DeclineMessageMutation,
  useDeclineMessageMutation,
} from '../../Views/VenueResponsePage/VenueResponsePage.generated'
import { useAccessSecret } from '../../Hooks/useAccessSecret'
import { CombinedError } from 'urql'
import { DeclineSource, EnquiryClosedSource } from '../../generated-types'
import { useCloseEnquiryMutation } from '../../Views/EventPage/CardContainer/CardContainer.generated'

const MIN_LENGTH = 3

const DialogTitle = styled(_DialogTitle)(({ theme }) => ({
  textAlign: 'center',
  [theme.breakpoints.up('sm')]: {
    fontSize: '1.5rem',
    padding: '48px 68px',
    paddingBottom: 32,
    lineHeight: 'calc(1.5rem / 24 * 32)',
  },
  fontSize: '1.125rem',
  padding: 30,
  lineHeight: 'calc(1.125rem / 18 * 24)',
}))

const HBButton = styled(_HBButton)(({ theme }) => ({
  '&:hover': {
    backgroundColor: '#c1544d',
  },
}))

const DialogSubtitle = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('sm')]: {
    lineHeight: 'calc(1rem / 16 * 24)',
  },
  whiteSpace: 'pre-wrap',
  textAlign: 'center',
  marginTop: 8,
  fontWeight: 400,
  fontSize: '1rem',
}))

const BackButton = styled('button')(({ theme }) => ({
  border: 'none',
  backgroundColor: 'transparent',
  padding: 0,
  margin: 0,
  outline: 'none',
  '&:hover': {
    cursor: 'pointer',
  },
  '.MuiChip-label': {
    fontWeight: '600',
  },
  '&:focus': {
    outline: '1px solid #1270B8',
  },
}))

const DeclineReasons = styled('ul')(({ theme }) => ({
  display: 'block',
  marginTop: 0,
  padding: 0,
  marginBottom: 24,
  listStyleType: 'none',
  '& > label': {
    display: 'block',
    marginLeft: '0',
    marginRight: '0',
    [theme.breakpoints.up('sm')]: {
      marginBottom: 4,
    },
  },
  'input[type="text"]': {
    marginLeft: '2rem',
  },
  '& > div:last-child': {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    input: {
      flexGrow: 1,
      padding: 5,
      minWidth: '200px',
    },
  },
  [theme.breakpoints.up('sm')]: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    marginBottom: 24,
    paddingLeft: 48 - 24,
    paddingRight: 48 - 24,
    '& > *': {
      minWidth: '38%',
    },
  },
}))

const LinkChip = styled(_LinkChip)(({ theme }) => ({
  color: '#1270B8',
  '&:hover': {
    color: '#1270B8',
    background: 'none',
  },
}))

interface CheckBoxProps {
  left?: boolean
}

const FormControlListItem = styled('li', {
  shouldForwardProp: (propName) => propName !== 'left',
})<CheckBoxProps>(({ left, theme }) => ({
  width: left ? '275px' : '216px',
  whiteSpace: 'nowrap',
  '& > .MuiFormControlLabel-root': {
    marginLeft: 0,
  },
}))

const Checkbox = styled(_Checkbox)(({ theme }) => ({
  '& *': {
    margin: '0',
  },
  'input[type="checkbox"]': {
    height: '0',
    width: '0',
  },
  '&:focus-within': {
    path: {
      outline: `1px solid ${theme.palette.primary.main}`,
    },
  },
}))

const ButtonGroup = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('sm')]: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'row-reverse',
    gap: '1rem',
    marginBottom: 24,
    marginLeft: 77,
    marginRight: 77,
    '& > button': {
      flexGrow: 1,
      width: '50%',
    },
  },
  textAlign: 'center',
  '& > button': {
    minHeight: '2.5rem',
    marginBottom: 8,
  },
}))

const ErrorText = styled('div')(({ theme }) => ({
  color: '#F44336',
  textAlign: 'center',
  width: '100%',
}))

const TextInput = styled('input')(({ theme }) => ({
  fontFamily: 'Montserrat',
  fontSize: '1rem',
  fontWeight: '400',
  border: '1px solid #585858',
  borderRadius: '4px',
  '&:focus': {
    outline: '1px solid #1270B8',
  },
}))

const CloseEnquiryGroup = styled(RadioGroup)(({ theme }) => ({
  marginBottom: '24px',
  justifyContent: 'center',
}))

const CloseEnquiryLabel = styled(FormLabel)(({ theme }) => ({
  marginTop: '16px',
  fontSize: '1.125rem',
  lineHeight: '1.5rem',
  fontWeight: 500,
  textAlign: 'center',
  [`&.${formLabelClasses.focused}`]: {
    color: 'initial',
  },
  [theme.breakpoints.up('sm')]: {
    fontSize: '1.5rem',
    lineHeight: '2rem',
  },
}))

interface DeclineModalProps {
  open: boolean
  enquiryActive: boolean
  messageId: number
  declineSource: DeclineSource
  onClose: () => void
  refetch: () => void
}

const mutationCompleteCheck = (
  fetching: boolean,
  data: DeclineMessageMutation | undefined,
  err: CombinedError | undefined
): boolean => {
  return !fetching && !!(data ?? err)
}

const isNotAlreadyDeclinedError = (err: CombinedError): boolean => {
  return !err.message.includes('Message already')
}

export const DeclineModal = (props: DeclineModalProps): JSX.Element => {
  const [{ fetching, data, error }] = useDeclineModalDeclineReasonsQuery({
    requestPolicy: 'cache-first',
  })

  const [
    { fetching: mutationFetching, data: mutationData, error: mutationError },
    declineMessage,
  ] = useDeclineMessageMutation()

  const [, closeEnquiry] = useCloseEnquiryMutation()

  const [reasons, setReasons] = useState<string[]>([])
  const [otherReasonChecked, setOtherReasonChecked] = useState(false)
  const [otherReason, setOtherReason] = useState('')
  const [closeEnquiryCheck, setCloseEnquiryCheck] = useState('continue')
  const [validationError, setValidationError] = useState<string | null>(null)
  const accessSecret = useAccessSecret()

  useEffect(() => {
    if (otherReason && !otherReasonChecked) {
      setOtherReasonChecked(true)
    }
  }, [otherReason, otherReasonChecked])

  const handleCloseEnquiry = async (): Promise<void> => {
    if (closeEnquiryCheck === 'continue' || !accessSecret) return

    await closeEnquiry({
      enquiryAccessSecret: accessSecret,
      closedSource: EnquiryClosedSource.Modal,
    })
  }

  const handleSubmit = (): void => {
    ;(async () => {
      const reasonsValid = reasons.length > 0 || otherReason.length > MIN_LENGTH
      if (!reasonsValid) {
        setValidationError('Please select at least one reason.')
        return
      }
      if (!accessSecret) {
        setValidationError('Something went wrong. Please try again later.')
        Sentry.captureException(new Error('DeclineModal: No access secret'))
        return
      }
      setValidationError(null)
      await handleCloseEnquiry()
      await declineMessage({
        messageId: props.messageId,
        accessSecret,
        reasons,
        declineSource: props.declineSource,
        otherReason,
      })
    })().catch((err) => {
      Sentry.captureException(err)
    })
  }

  if (error && !data) {
    throw new Error(error.message)
  }

  if (!data && !fetching) {
    throw new Error('No data returned!')
  }

  if (mutationCompleteCheck(mutationFetching, mutationData, mutationError)) {
    if (mutationError && isNotAlreadyDeclinedError(mutationError)) {
      throw new Error(mutationError.message)
    }
    props.refetch()
    props.onClose()
  }

  return (
    <StyledDialog open={props.open} onClose={props.onClose}>
      <DialogTitle>
        Why is this venue not a good match?
        <DialogSubtitle>
          {`Feedback will be shared with them.\nSelect one or more reasons.`}
        </DialogSubtitle>
      </DialogTitle>
      <DialogContent>
        <DeclineReasons>
          {data && (
            <>
              {data.declineReasons.map(({ text, id }, i) => (
                <FormControlListItem left={i % 2 === 0} key={id}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name='reason'
                        value={id}
                        checked={reasons.indexOf(id) !== -1}
                        onChange={() =>
                          setReasons(
                            reasons.indexOf(id) !== -1
                              ? reasons.filter((r) => r !== id)
                              : [...reasons, id]
                          )
                        }
                      />
                    }
                    label={text}
                  />
                </FormControlListItem>
              ))}
              <div>
                <Checkbox
                  name='reason'
                  value='other'
                  id='decline-reasons-other'
                  checked={otherReasonChecked}
                  onClick={() => {
                    setOtherReasonChecked(!otherReasonChecked)
                    if (otherReasonChecked) {
                      setOtherReason('')
                    }
                  }}
                />
                <label htmlFor='decline-reasons-other'>Other reason:</label>
                <TextInput
                  type='text'
                  aria-label='Other reasons for declining'
                  value={otherReason}
                  onChange={(e) => {
                    setOtherReason(e.target.value)
                  }}
                  onBlur={() => {
                    setOtherReason(otherReason.trim())
                    if (!otherReason.trim().length) {
                      setOtherReasonChecked(false)
                    }
                  }}
                />
                {validationError && <ErrorText>{validationError}</ErrorText>}
              </div>
            </>
          )}
        </DeclineReasons>
        {props.enquiryActive && (
          <FormControl component='fieldset'>
            <CloseEnquiryLabel id='close-enquiry-label' as='legend'>
              Do you want to continue receiving messages from new venues?
            </CloseEnquiryLabel>
            <CloseEnquiryGroup
              aria-labelledby='close-enquiry-label'
              defaultValue='continue'
              name='close-enquiry-buttons'
              onChange={(e) => setCloseEnquiryCheck(e.target.value)}
              row
            >
              <FormControlLabel
                value='continue'
                control={<Radio />}
                label='Yes, continue'
              />
              <FormControlLabel
                value='close'
                control={<Radio />}
                label='No, I have enough'
              />
            </CloseEnquiryGroup>
          </FormControl>
        )}

        <ButtonGroup>
          <HBButton
            aria-disabled={
              reasons.length === 0 && otherReason.length < MIN_LENGTH
            }
            onClick={handleSubmit}
            disabled={fetching}
          >
            Submit
          </HBButton>
          <BackButton onClick={props.onClose} disabled={fetching}>
            <LinkChip
              icon={<ChevronIcon />}
              label='Back to message'
              largeLabel='Back to message'
            />
          </BackButton>
        </ButtonGroup>
      </DialogContent>
    </StyledDialog>
  )
}
